import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import unauthenticatedFetch from 'services/rest/unauthenticatedFetch';
import { FeatureFlagKey } from 'services/statsig/types';
import { Headers } from 'shared/constants/headers';
import {
  ClientError,
  isBaseNetworkError,
  ServerError,
} from 'shared/utils/errors';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';

export interface CreateAccountInput {
  aToken?: string;
  anonymousId: string;
  emailAddress: string;
  password: string;
  phoneNumber: string;
}

export interface CreateAccountResponse {
  challenge: string;
  phoneChange: string;
  phoneLineNumber: string;
  session: string;
  userId: string;
  version: string;
  idToken: string;
  title: string;
  errorCode: string;
  message: string;
}

const postCreateAccountRequest = async (
  lambdaUrl: string,
  data: CreateAccountInput,
  headers: Headers,
): Promise<CreateAccountResponse> => {
  const reqHeaders = {
    ...headers,
  };

  try {
    const response = await unauthenticatedFetch(`${lambdaUrl}/signup`, {
      method: 'POST',
      cache: 'no-cache',
      body: JSON.stringify({
        ...data,
      }),
      headers: reqHeaders,
    });
    return response.json() as Promise<CreateAccountResponse>;
  } catch (error) {
    if (isBaseNetworkError(error)) {
      const status =
        error instanceof ClientError || error instanceof ServerError
          ? error.statusCode
          : undefined;
      const body = (
        error instanceof ClientError || error instanceof ServerError
          ? error.responseBody
          : undefined
      ) as CreateAccountResponse | undefined;

      if (body?.errorCode) {
        if (!status || status < 400 || status >= 500) {
          logBreadcrumb({
            level: 'error',
            message: `signup request error, status: ${status}, errorCode: ${body.errorCode}, title: ${body.title}, message: ${body.message}`,
          });
        } else {
          logError(`signup request error: ${status}`);
        }
        return body;
      }
    }
    throw error;
  }
};

function useCreateAccount() {
  const authUrl = useAuthUrl(FeatureFlagKey.SIGNUP_USE_K8S_AUTH_SERVICE);

  const postCreateAccount = async (
    data: CreateAccountInput,
    headers: Headers,
  ) => {
    return await postCreateAccountRequest(authUrl, data, headers);
  };
  return { postCreateAccount };
}

export default useCreateAccount;
